import { StrictMode, lazy, Suspense } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import {
  RouterProvider,
  createRouter,
  createBrowserHistory,
  createMemoryHistory,
} from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { IressShadow } from '@iress-oss/ids-components';
import { routeTree } from './routeTree.gen';

import { queryClient } from '@/lib/query/queryClient';
import { tenantConfigQueryOptions } from '@/lib/query/tenantConfigQuery';

const ReactQueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then((m) => ({
    default: m.ReactQueryDevtools,
  })),
);

export const ELEMENT_NAME = 'xplan-meeting-assistant';

export const CLOUD_APP_PREFIX = `/cloud_app/${ELEMENT_NAME}`;

export function detectBasepath(): string {
  const { pathname } = window.location;
  return pathname === CLOUD_APP_PREFIX ||
    pathname.startsWith(`${CLOUD_APP_PREFIX}/`)
    ? CLOUD_APP_PREFIX
    : '/';
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createRouter>;
  }
}

export function createHistoryForMode(mode: string) {
  return mode === 'memory' ? createMemoryHistory() : createBrowserHistory();
}

export function stringifySearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const str = params.toString();
  return str ? `?${str}` : '';
}

export function parseSearch(searchStr: string): Record<string, string> {
  const params = new URLSearchParams(searchStr);
  const result: Record<string, string> = {};
  for (const [key, value] of params.entries()) {
    result[key] = value;
  }
  return result;
}

/**
 * Custom element that hosts the Xplan Meeting Assistant as a self-contained
 * microfrontend. Uses IressShadow for style-isolated shadow DOM and TanStack
 * Router. Supports a `history-mode` attribute: "browser" (default) for clean
 * URLs in standalone mode, or "memory" for embedded contexts where the host
 * app owns the URL bar.
 */
class XplanMeetingAssistantElement extends HTMLElement {
  private root: Root | null = null;

  connectedCallback() {
    void queryClient.prefetchQuery(tenantConfigQueryOptions);

    const container = document.createElement('div');
    const navHeader = document.querySelector('nav.header');
    let toasterOffset: string | undefined;
    if (navHeader) {
      const navHeight = navHeader.getBoundingClientRect().height;
      container.style.setProperty(
        '--app-height',
        `calc(100dvh - ${navHeight}px - 1rem)`,
      );
      toasterOffset = `${navHeight}px`;
    }
    this.appendChild(container);

    const historyMode = this.getAttribute('history-mode') ?? 'browser';
    const router = createRouter({
      routeTree,
      history: createHistoryForMode(historyMode),
      basepath: detectBasepath(),
      defaultPreload: 'intent',
      stringifySearch,
      parseSearch,
    });

    this.root = createRoot(container);
    this.root.render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <IressShadow toasterOffset={toasterOffset}>
            <RouterProvider router={router} />
          </IressShadow>
          {import.meta.env.DEV && (
            <Suspense>
              <ReactQueryDevtools />
            </Suspense>
          )}
        </QueryClientProvider>
      </StrictMode>,
    );
  }

  disconnectedCallback() {
    this.root?.unmount();
    this.root = null;
  }
}

async function enableMocking() {
  if (!import.meta.env.DEV) return;
  if (import.meta.env.VITE_API_MOCKING !== 'true') return;

  try {
    const { initMocks } = await import('@/lib/mocks');
    await initMocks();
  } catch (error) {
    console.error('Failed to initialize API mocks', error);
  }
}

void enableMocking().then(() => {
  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, XplanMeetingAssistantElement);
  }
});
